import { Box, Button, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import Table, { ColumnType } from "components/Table";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ResType } from "types";
import Loader from "components/Loader";
import { formatToRupeess } from "utils/formattedAmount";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { challanPaymentTax, getAutFinancialYears, sortFilter } from "./constants";
import SearchContainer from "components/SearchContainer";
import moment from "moment";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import useTitle from "hooks/useTitle";
import IncomeTaxEChallanInfo from "views/atom-pro/incomtaxEChallanInfo";
import CampaignIcon from "@mui/icons-material/Campaign";
import { getIncomeTaxEchallans } from "api/services/automation/income-tax-tan";
import { usePermissions } from "context/PermissionsProvider";
import AccessDenied from "views/atom-pro/Access/accessDenide";
import { Permissions } from "data/permissons";
import { exportTanIncomeTaxChallans } from "api/services/automation/income-tax-tan";
import { getCurrentFormattedDateTime } from "utils";
import { link } from "fs";
import { handleError } from "utils/handleError";
import { snack } from "components/toast";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { pl } from "date-fns/locale";
import { SortDirection } from "data/constants";
import _ from "lodash";
import sortHandler from "components/Table/SortHandler";




const TanIncomeTaxChallans = (state) => {
  useTitle("ATOM Pro | e-Challans");
  const params = useParams();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [assessmentYear, setAssessmentYear] = useState<any>(null);
  const [clientCategory, setClientCategory] = useState<any>(null);
  const [sortValue, setSort] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [openInfo, setOpenInfo] = useState(false);
  const defaultColumns: Array<ColumnType> = [
    {
      key: "id",
      title: "AY",
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
      render: (row: any) => {
        return (
          <Box>
            <Typography>{row?.assessmentYear ? `${row?.assessmentYear}` : ""}</Typography>
          </Box>
        );
      },
    },
    {
      key: "name",
      title: (
        <Box display="flex" alignItems="center">
          <PersonOutlineOutlinedIcon style={{ width: "20px", marginRight: "5px" }} />Client Name (TAN)
        </Box>
      ),
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
      render: (v) => (
        <Typography>
          {v?.tanClientCredentials?.client?.displayName} (<span>{v?.tan}</span>)
        </Typography>
      ),
    },
    {
      key: "minorDesc",
      title: "Type of Payment",
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
      render: (row: any) => {
        return (
          <Box>
            <Typography>
              {row?.minorDesc ? `${row?.minorDesc} (${row?.minorHead})` : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: "natureOfPayment",
      title: "Nature of Payment",
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
      render: (row: any) => {
        return (
          <Box>
            <Typography>
              {row?.natureOfPayment ? `${row?.natureOfPayment}` : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: "paymentTime",
      title: "Date of Payment",
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
      render: (row: any) => {
        return (
          <Typography>
            {row?.paymentTime ? moment(row?.paymentTime).format("MMM DD, YYYY") : "-"}
          </Typography>
        );
      },
    },
    {
      key: "acin",
      title: (
        <Box display="flex" alignItems="center">
          <Typography>Alternate CIN</Typography>
          <Tooltip title="Alternate CIN has first 7 digits as BSR code of bank followed by date of payment in 8 digits and further followed by 5 digit challan number">
            <HelpOutlineIcon fontSize="small" sx={{ marginLeft: "5px" }} />
          </Tooltip>
        </Box>
      ),
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
    },

    {
      key: "totalAmt",
      title: "Total Amount",
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
      render: (row: any) => {
        return (
          <Tooltip
            title={`Tax: ${row?.basicTax} + Surcharge: ${row?.surCharge} + Cess: ${row?.eduCess
              } + Interest: ${row?.interest} + Penalty: ${row?.penalty} + Others: ${row?.others
              } = Total Amount: ${formatToRupeess(row?.totalAmt)}`}
          >
            <Typography>
              <Box display="flex" alignItems="center">
                <CurrencyRupeeIcon sx={{ width: "15px" }} /> {formatToRupeess(row?.totalAmt)}
              </Box>
            </Typography>
          </Tooltip>
        );
      },
    },

  ];
  const [sort, setSorting] = useState<any>({});
  const [columns, setColumns] = useState(_.cloneDeep(defaultColumns));
  const { data, isLoading }: ResType = useQuery(
    [
      "get-tan-e-challans",
      {
        limit: pageCount,
        offset: page * pageCount,
        search,
        sortValue,
        assessmentYear,
        clientCategory,
        sort
      },
    ],
    getIncomeTaxEchallans,
    {
      onSuccess: (res) => {
        setTotalCount(res?.data?.count);
      },
      // enabled: permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)
    }
  );


  const { mutate: mutate1 } = useMutation(exportTanIncomeTaxChallans, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const file = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      const formattedDateTime = getCurrentFormattedDateTime();
      link.href = file;
      link.download = `ATOM Pro - Income Tax (TAN) Challans ${formattedDateTime}.xlsx`;
      link.click();
      snack.success("Income Tax (TAN) Challans exported successfully");
    },
    onError: (err: any) => {
      snack.error(err);
      snack.error(handleError(err));
    },
  });
  const handleExport = () => {
    mutate1({
      ...state,
      search,
      sortValue,
      assessmentYear,
      clientCategory,
    });
  };


  const onAssessmentYearChange = (e: any) => {
    setAssessmentYear(e.target.value);
    setPage(0);
  };

  const onClientCategoryChange = (e: any) => {
    setClientCategory(e.target.value);
    setPage(0);
  };

  const onSortChange = (e: any) => {
    setPage(0);
    setSort(e.target.value);
  };

  const handleSearch = (e: any) => {
    setPage(0);
    setSearch(e);
  };




    const handleSort = (v: any) => {
      sortHandler({
        key: v.key,
        columns,
        sortState: sort,
        setSortState: setSorting,
        setColumns: setColumns,
      });
    };
    
  const handleRowClick = (v: any) => {
    navigate(`/atom-pro/income-tax-tan/e-challan/${v?.id}`);
  };

  // if (!(permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS))) {
  //   return <AccessDenied />
  // }

  if (isLoading) return <Loader />;
  return (
    <>
      <Box display="flex" m={1}>
        <Box ml={1}>
          <TextField
            name="assessmentYear"
            value={assessmentYear}
            onChange={onAssessmentYearChange}
            size="small"
            label="Assessment Year"
            select
            sx={{ width: 150 }}
          >
            <MenuItem key={""} value={""}>
              Select
            </MenuItem>
            <MenuItem key={""} value={"null"}>
              NA
            </MenuItem>
            {getAutFinancialYears().map((year: string) => (
              <MenuItem key={year} value={year.split("-")[0]}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {/* <Box ml={1}>
          <TextField
            name="clientCategory"
            value={clientCategory}
            onChange={onClientCategoryChange}
            size="small"
            label="Payment Type"
            select
            sx={{ width: 120 }}
          >
            <MenuItem key={""} value={""}>
              Select
            </MenuItem>
            {challanPaymentTax.map((category) => (
              <MenuItem key={category.value} value={category.value}>
                {category.label}
              </MenuItem>
            ))}
          </TextField>
        </Box> */}
        <Box ml={1}>
          <TextField
            name="sortValue"
            value={sortValue}
            onChange={onSortChange}
            size="small"
            label="Sort By"
            select
            sx={{ width: 100 }}
          >
            <MenuItem key={""} value={""}>
              Select
            </MenuItem>
            {sortFilter.map((sort) => (
              <MenuItem key={sort.value} value={sort.value}>
                {sort.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box ml={1}>
          <SearchContainer
            minWidth="250px"
            debounced
            value={search}
            placeHolder="Search by Client | TAN"
            onChange={handleSearch}
          />
        </Box>
        <Box ml={1}>
          <Button
            onClick={() => {
              setAssessmentYear(null);
              setClientCategory(null);
              setSort(null);
              setSearch("");
              setInterval("");
            }}
            size="medium"
            color="secondary"
            variant="outlined"
          // sx={{ width: 120 }}
          >
            Clear Filters
          </Button>
        </Box>
        <Button onClick={() => setOpenInfo(true)} variant="outlined" sx={{ mr: 3, ml: 3 }}>
          <CampaignIcon />
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleExport}
          startIcon={<FileDownloadOutlinedIcon />}
        >
          Export
        </Button>

        <IncomeTaxEChallanInfo open={openInfo} setOpen={setOpenInfo} />
      </Box>
      <Box>
        {data && data?.data.count > 0 ? (
          <>
            <Table
              sx={{ height: '530px', mr: 1, ml: 1 }}
              pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
              data={data?.data?.result || []}
              columns={columns}
              sortHandler={handleSort}
              loading={isLoading}
              onRowClick={handleRowClick}
            />
          </>
        ) : (
          <>
            <Box textAlign="center" mt={20}>
              <Typography variant="subtitle1" color="rgba(0,0,0,0.5)">
                No Data present
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default TanIncomeTaxChallans;
